import { ChevronDown, ChevronRight, X } from 'lucide-react';
import * as React from 'react';
import { formatDuration, type DayBookability, type Suggestion, type SuggestionGroup } from '@cfl/shared';

import {
  Alert, AlertDescription, Button, Checkbox, Combobox,
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { CalendarArt } from '@/components/illustrations';
import { Hint } from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import {
  MAX_ACCEPT_ITEMS,
  useAcceptSuggestions, useCalendarSuggestions, useDismissSuggestions, useRestoreSuggestions,
  type AcceptItem,
} from '@/lib/calendar-api';
import { fitsProject, useActivities, useProjects, type ProjectRow } from '@/lib/catalog-api';
import { zonedParts } from '@/lib/zoned-instant';

/** A group's project/activity choice while it is being decided on. */
interface Pairing {
  projectId: string;
  activityId: string;
}

/**
 * The triage panel: one row per meeting SERIES, not per occurrence.
 *
 * Opens over the grid rather than beside it. Suggestions never touch the
 * grid's own rows/cells/totals (see calendar-banner.tsx and RULING 3 in the
 * superseded suggestion-rows.tsx) - this component is the only place a
 * calendar suggestion is rendered at all.
 */
export function CalendarPanel({
  open,
  onOpenChange,
  week,
  timeZone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  week: string;
  /** The signed-in person's own timezone - see the long comment this carried
   *  forward from suggestion-rows.tsx's `timeZone` prop docs. */
  timeZone: string;
}) {
  const { data } = useCalendarSuggestions(week);
  const { data: projects } = useProjects();
  const { data: activities } = useActivities();
  const accept = useAcceptSuggestions();
  const dismiss = useDismissSuggestions();
  const restore = useRestoreSuggestions();

  const [pairings, setPairings] = React.useState<Record<string, Pairing>>({});
  const [ticked, setTicked] = React.useState<Record<string, Set<string>>>({});
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  // Which group an error belongs to. Shown inside that group's card: the sheet
  // scrolls, and a message at its top is out of sight of the Accept that caused it.
  const [errorKey, setErrorKey] = React.useState<string | null>(null);
  const fail = (key: string | null, err: unknown, fallback: string) => {
    setErrorKey(key);
    setError(err instanceof ApiError ? err.message : fallback);
  };
  const [showDismissed, setShowDismissed] = React.useState(false);

  const groups = data?.groups ?? [];
  const days = data?.days ?? [];
  const bookableByDate = new Map(days.map((d) => [d.day, d]));
  const noWorkingDays = days.length > 0 && days.every((d) => !d.bookable);

  // Seed pairing + ticks for groups we have not seen state for yet, without
  // clobbering a choice already made on screen (e.g. after refetch when one
  // group of the batch was just accepted).
  React.useEffect(() => {
    const seedTicks = (g: SuggestionGroup) => {
      // Pre-tick every occurrence whose day is bookable; a holiday or a
      // non-working day arrives unticked but not disabled (dayBookability's
      // own contract - see packages/shared/src/calendar.ts).
      const set = new Set<string>();
      for (const occ of g.occurrences) {
        const { workDate } = zonedParts(occ.startsAt, timeZone);
        const bookability = bookableByDate.get(workDate);
        if (bookability?.bookable ?? true) set.add(occ.eventId);
      }
      return set;
    };

    setPairings((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of groups) {
        if (!(g.key in next)) {
          next[g.key] = { projectId: g.projectId ?? '', activityId: g.activityId ?? '' };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setTicked((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of groups) {
        if (!(g.key in next)) {
          next[g.key] = seedTicks(g);
          changed = true;
          continue;
        }
        // Prune ticks for ids the refetch no longer lists on this group - the
        // accept path (and the dismiss handlers, which also shrink a group)
        // can both leave a stale id behind after a partial write: acceptGroup
        // never clears `ticked[group.key]` itself, on purpose, because a
        // refetch must not clobber an on-screen choice for a group nobody
        // touched. But "the group's own occurrences" is always the true set,
        // so pruning against THAT rather than trying to guess which items
        // were written/dismissed is the only form that cannot drift from
        // what is on screen.
        const live = new Set(g.occurrences.map((o) => o.eventId));
        const pruned = new Set([...next[g.key]!].filter((id) => live.has(id)));
        if (pruned.size !== next[g.key]!.size) {
          // A group pruned down to nothing must re-seed from bookability
          // exactly as a fresh group does, not sit there permanently empty -
          // otherwise a group that survives a partial accept with new,
          // never-ticked occurrences would render with everything unticked
          // and no way to tell that apart from a deliberate "untick all".
          next[g.key] = pruned.size > 0 ? pruned : seedTicks(g);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bookableByDate is rebuilt every render from `days`, which is itself in `groups`' dependency shadow via `data`
  }, [groups, timeZone]);

  if (!data) return null;

  const setPairing = (key: string, next: Partial<Pairing>) =>
    setPairings((prev) => ({ ...prev, [key]: { ...prev[key]!, ...next } }));

  const toggleDay = (groupKey: string, eventId: string) =>
    setTicked((prev) => {
      const set = new Set(prev[groupKey] ?? []);
      if (set.has(eventId)) set.delete(eventId);
      else set.add(eventId);
      return { ...prev, [groupKey]: set };
    });

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const buildItem = (pairing: Pairing, s: Suggestion): AcceptItem => {
    const { workDate, clock } = zonedParts(s.startsAt, timeZone);
    return {
      eventId: s.eventId,
      matchKey: s.matchKey,
      workDate,
      projectId: pairing.projectId,
      activityId: pairing.activityId,
      durationSeconds: s.durationSeconds,
      beginTime: clock,
      description: s.title,
    };
  };

  const acceptGroup = async (group: SuggestionGroup) => {
    const pairing = pairings[group.key];
    if (!pairing?.projectId || !pairing.activityId) return;
    const tickedIds = ticked[group.key] ?? new Set<string>();
    const items = group.occurrences
      .filter((o) => tickedIds.has(o.eventId))
      .map((o) => buildItem(pairing, o));
    if (items.length === 0) return;

    setError(null);
    setBusyKey(group.key);
    try {
      await accept.mutateAsync(items);
    } catch (err) {
      fail(group.key, err, 'Could not accept that meeting.');
    } finally {
      setBusyKey(null);
    }
  };

  // Dismiss/restore both invalidate ['calendar','suggestions'] (see
  // calendar-api.ts), so the refetch is what updates dismissedSuggestions - no
  // local bookkeeping of "what did I just dismiss" is needed any more; the
  // server's next answer already reflects it, and reflects it correctly even
  // after a reload, which client state cannot. The same refetch is also what
  // the ticked-pruning effect above runs against, since dismissing occurrences
  // out of a group shrinks it the same way a partial accept does.
  const dismissGroup = async (group: SuggestionGroup) => {
    setError(null);
    setBusyKey(`dismiss:${group.key}`);
    try {
      await dismiss.mutateAsync({
        eventIds: group.occurrences.map((o) => o.eventId),
        week,
      });
    } catch (err) {
      fail(group.key, err, 'Could not dismiss that meeting.');
    } finally {
      setBusyKey(null);
    }
  };

  const dismissOccurrence = async (occ: Suggestion) => {
    setError(null);
    setBusyKey(`dismiss:${occ.eventId}`);
    try {
      await dismiss.mutateAsync({ eventIds: [occ.eventId], week });
    } catch (err) {
      const owner = groups.find((g) => g.occurrences.some((o) => o.eventId === occ.eventId));
      fail(owner?.key ?? null, err, 'Could not dismiss that meeting.');
    } finally {
      setBusyKey(null);
    }
  };

  const restoreOne = async (eventId: string) => {
    setError(null);
    setBusyKey(`restore:${eventId}`);
    try {
      await restore.mutateAsync({ eventIds: [eventId] });
    } catch (err) {
      fail(null, err, 'Could not restore that meeting.');
    } finally {
      setBusyKey(null);
    }
  };

  const readyForAll =
    groups.length > 0 &&
    groups.every((g) => {
      const pairing = pairings[g.key];
      const tickedIds = ticked[g.key] ?? new Set<string>();
      return pairing?.projectId && pairing.activityId && tickedIds.size > 0;
    });

  // Why Accept all is off, by meeting name. A disabled button's hint never shows,
  // so without this it was greyed out with nothing saying which meeting to fix.
  const notReady = groups.flatMap((g) => {
    const pairing = pairings[g.key];
    const name = `"${g.title ?? 'Untitled meeting'}"`;
    if ((ticked[g.key]?.size ?? 0) === 0) return [`${name} (no day ticked)`];
    if (!pairing?.projectId || !pairing.activityId) return [`${name} (no project or activity)`];
    return [];
  });

  // What Accept All would actually post: the sum of ticked days across every
  // group that has a complete project/activity pairing. Computed here, before
  // posting, because the API's own cap (`acceptBody.items.max(50)` in
  // timesheet.controller.ts) fails closed with a Zod error that names no
  // number - see MAX_ACCEPT_ITEMS's doc comment. A real week of 17 groups
  // averaging 3 occurrences is 51 items, comfortably over, so this is not a
  // corner case.
  const acceptAllCount = groups.reduce((sum, g) => {
    const pairing = pairings[g.key];
    if (!pairing?.projectId || !pairing.activityId) return sum;
    const tickedIds = ticked[g.key] ?? new Set<string>();
    return sum + g.occurrences.filter((o) => tickedIds.has(o.eventId)).length;
  }, 0);
  const overCap = acceptAllCount > MAX_ACCEPT_ITEMS;

  const acceptAll = async () => {
    if (overCap) return;
    const items: AcceptItem[] = [];
    for (const g of groups) {
      const pairing = pairings[g.key];
      if (!pairing?.projectId || !pairing.activityId) continue;
      const tickedIds = ticked[g.key] ?? new Set<string>();
      for (const occ of g.occurrences) {
        if (tickedIds.has(occ.eventId)) items.push(buildItem(pairing, occ));
      }
    }
    if (items.length === 0) return;
    setError(null);
    setBusyKey('__all__');
    try {
      await accept.mutateAsync(items);
    } catch (err) {
      fail(null, err, 'Could not accept those meetings.');
    } finally {
      setBusyKey(null);
    }
  };

  const projectOptions = (projects ?? []).map((p) => ({
    value: p.id,
    label: p.name,
    description: p.customer.name,
  }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Meetings from your calendar</SheetTitle>
        </SheetHeader>

        {error && errorKey === null && (
          <Alert className="border-destructive/30">
            <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
          </Alert>
        )}

        {/* The no-contract case: every day comes back not-bookable because
            nothing expects work from this person at all (no WorkingContract),
            which is plausible for a new hire HR has not set up yet. Say so
            plainly rather than rendering rows nobody can tick - but the panel
            still works for dismissing, so it is not disabled outright. */}
        {noWorkingDays && (
          <Alert className="border-muted-foreground/20">
            <AlertDescription className="text-sm leading-relaxed text-muted-foreground">
              No working days are set up for this week, so nothing here can be
              booked yet. HR sets up your contract - you can still dismiss
              meetings you do not want to see again.
            </AlertDescription>
          </Alert>
        )}

        {groups.length === 0 ? (
          <EmptyState size="sm" art={CalendarArt} title="Nothing left to review this week" />
        ) : (
          <div className="space-y-3">
            {groups.map((group) => (
              <GroupRow
                key={group.key}
                group={group}
                timeZone={timeZone}
                days={days}
                pairing={pairings[group.key] ?? { projectId: '', activityId: '' }}
                tickedIds={ticked[group.key] ?? new Set()}
                expanded={expanded.has(group.key)}
                busy={busyKey === group.key || busyKey === '__all__'}
                busyKey={busyKey}
                projectOptions={projectOptions}
                activities={activities ?? []}
                projects={projects}
                onSetPairing={(next) => setPairing(group.key, next)}
                onToggleDay={(eventId) => toggleDay(group.key, eventId)}
                onToggleExpanded={() => toggleExpanded(group.key)}
                onAccept={() => void acceptGroup(group)}
                onDismissGroup={() => void dismissGroup(group)}
                onDismissOccurrence={(occ) => void dismissOccurrence(occ)}
                error={errorKey === group.key ? error : null}
              />
            ))}
          </div>
        )}

        {groups.length > 0 && (
          <div className="space-y-1">
            <Button hint={"Add every suggested meeting to your timesheet"}
              disabled={!readyForAll || overCap || busyKey !== null}
              onClick={() => void acceptAll()}
            >
              {busyKey === '__all__'
                ? 'Accepting…'
                : overCap
                  ? `Accept all (${acceptAllCount}) — too many at once`
                  : `Accept all (${acceptAllCount})`}
            </Button>
            {/* Names both numbers, per CLAUDE.md's "errors name the thing" -
                a refusal that just says "too many" leaves the person guessing
                how many to untick. Per-group Accept is never capped here: it
                posts at most one group's occurrences (5 in practice), far
                under the API's limit, so only the bulk button can hit it. */}
            {!readyForAll && notReady.length > 0 && (
              <p className="text-xs text-destructive">
                Accept all needs every meeting ready. Fix or dismiss: {notReady.join(', ')}.
              </p>
            )}
            {overCap && (
              <p className="text-xs text-muted-foreground">
                The limit is {MAX_ACCEPT_ITEMS} at once. Accept a few groups first, then Accept
                all for the rest.
              </p>
            )}
          </div>
        )}

        {/*
         * The footer that makes dismissal reachable, and reachable AFTER a
         * reload - not just within the panel session that dismissed
         * something. `dismissedSuggestions` is server-provided (Fix round 1):
         * the API already fetches these from Google to compute `suggestions`
         * above, and simply keeps the ones the dismissal filter drops instead
         * of throwing them away, rather than this component remembering what
         * IT watched happen. Nothing new is persisted - see
         * apps/api/src/calendar/suggestion.service.ts.
         *
         * The header count uses `dismissedSuggestions.length`, which is the
         * ONLY count the API sends: a dismissal can outlive its event (the
         * meeting is later deleted or moved off this week in Google), so a
         * raw row count would promise a Show list one item longer than what
         * can actually be listed and restored. A number with nothing to back
         * part of it is worse than a slightly smaller, fully explained one -
         * which is also why that raw count is not returned here at all,
         * rather than kept as a field nothing reads.
         */}
        <div className="mt-auto border-t pt-3 text-sm text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>{data.dismissedSuggestions.length} dismissed this week</span>
            {data.dismissedSuggestions.length > 0 && (
              <Button hint={"Show the meetings you dismissed"} size="sm" variant="ghost" onClick={() => setShowDismissed((v) => !v)}>
                {showDismissed ? 'Hide' : 'Show'}
              </Button>
            )}
          </div>
          {showDismissed && (
            <div className="mt-2 space-y-1">
              {data.dismissedSuggestions.map((s) => (
                <div key={s.eventId} className="flex items-center justify-between gap-2 rounded-md bg-muted/30 px-2 py-1">
                  <span className="truncate">{s.title ?? 'Untitled meeting'}</span>
                  <Button hint={"Offer this meeting again"}
                    size="sm"
                    variant="outline"
                    disabled={busyKey === `restore:${s.eventId}`}
                    onClick={() => void restoreOne(s.eventId)}
                  >
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function GroupRow({
  group,
  timeZone,
  days,
  pairing,
  tickedIds,
  expanded,
  busy,
  busyKey,
  projectOptions,
  activities,
  projects,
  onSetPairing,
  onToggleDay,
  onToggleExpanded,
  onAccept,
  onDismissGroup,
  onDismissOccurrence,
  error,
}: {
  group: SuggestionGroup;
  timeZone: string;
  days: DayBookability[];
  pairing: Pairing;
  tickedIds: Set<string>;
  expanded: boolean;
  busy: boolean;
  busyKey: string | null;
  projectOptions: { value: string; label: string; description?: string }[];
  activities: { id: string; name: string; project: { id: string } | null }[];
  projects: ProjectRow[] | undefined;
  onSetPairing: (next: Partial<Pairing>) => void;
  onToggleDay: (eventId: string) => void;
  onToggleExpanded: () => void;
  onAccept: () => void;
  onDismissGroup: () => void;
  onDismissOccurrence: (occ: Suggestion) => void;
  error: string | null;
}) {
  const bookableByDate = new Map(days.map((d) => [d.day, d]));
  const first = group.occurrences[0]!;
  const ready = Boolean(pairing.projectId && pairing.activityId && tickedIds.size > 0);
  const total = group.occurrences
    .filter((o) => tickedIds.has(o.eventId))
    .reduce((sum, o) => sum + o.durationSeconds, 0);

  return (
    <div className="space-y-2 rounded-lg border bg-background/60 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{group.title ?? 'Untitled meeting'}</div>
          <div className="ts-num text-xs text-muted-foreground">
            {zonedParts(first.startsAt, timeZone).clock}–{zonedParts(first.endsAt, timeZone).clock}
            {' · '}
            {formatDuration(first.durationSeconds)} per occurrence
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Dismiss this meeting for the week"
          title="Dismiss - stays dismissed, and the footer below can bring it back"
          disabled={busy}
          onClick={onDismissGroup}
        >
          <X className="size-3.5" />
        </Button>
      </div>

      {/* One checkbox per occurrence day. Days are Mon-Sun in `days`, but a
          group only shows the days it actually occurs on - a group with one
          occurrence gets one checkbox, not seven. */}
      <div className="flex flex-wrap gap-2">
        {group.occurrences.map((occ) => {
          const { workDate } = zonedParts(occ.startsAt, timeZone);
          const bookability = bookableByDate.get(workDate);
          const label = zonedWeekdayLabel(occ.startsAt, timeZone);
          return (
            <label
              key={occ.eventId}
              className="flex items-center gap-1.5 rounded-md border border-transparent px-1.5 py-1 text-xs hover:border-muted-foreground/20"
              title={bookability && !bookability.bookable ? `${label}: ${bookability.reason ?? 'not bookable'}` : undefined}
            >
              <Checkbox
                checked={tickedIds.has(occ.eventId)}
                onCheckedChange={() => onToggleDay(occ.eventId)}
                disabled={busy}
              />
              <span>{label}</span>
              {bookability && !bookability.bookable && (
                <span className="text-amber-600 dark:text-amber-500">({bookability.reason})</span>
              )}
            </label>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-40">
          <Combobox
            value={pairing.projectId || null}
            onChange={(v) => {
              const stillValid = activities.some(
                (a) => a.id === pairing.activityId && (fitsProject(a, v, projects)),
              );
              onSetPairing({ projectId: v, activityId: stillValid ? pairing.activityId : '' });
            }}
            options={projectOptions}
            placeholder="Project"
            searchPlaceholder="Search projects"
            disabled={busy}
          />
        </div>
        <div className="w-36">
          <Combobox
            value={pairing.activityId || null}
            onChange={(v) => onSetPairing({ activityId: v })}
            options={activities
              .filter((a) => fitsProject(a, pairing.projectId, projects))
              .map((a) => ({ value: a.id, label: a.name }))}
            placeholder={pairing.projectId ? 'Activity' : 'Project first'}
            searchPlaceholder="Search activities"
            disabled={busy || !pairing.projectId}
          />
        </div>
        <Button hint={"Add the ticked occurrences of this meeting to your timesheet"} size="sm" disabled={!ready || busy} onClick={onAccept}>
          {busyKey === group.key ? 'Accepting…' : 'Accept'}
        </Button>
      </div>
      {error && <p role="alert" className="text-sm font-medium text-destructive">{error}</p>}

      <div className="ts-num text-xs text-muted-foreground">
        {tickedIds.size} × {formatDuration(first.durationSeconds)} = {formatDuration(total)}
      </div>

      {group.occurrences.length > 1 && (
        <Hint label={'Show each occurrence'}><button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={onToggleExpanded}
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {group.occurrences.length} occurrences
        </button></Hint>
      )}

      {expanded && (
        <div className="space-y-1.5 border-t pt-2">
          {group.occurrences.map((occ) => (
            <div
              key={occ.eventId}
              className="flex flex-wrap items-center gap-2 rounded-md bg-muted/20 px-2 py-1.5 text-xs"
            >
              <div className="min-w-[120px] flex-1">
                <div className="ts-num">
                  {zonedParts(occ.startsAt, timeZone).workDate}
                  {' · '}
                  {zonedParts(occ.startsAt, timeZone).clock}–{zonedParts(occ.endsAt, timeZone).clock}
                </div>
                {occ.overlapsSeconds > 0 && (
                  <div className="text-amber-600 dark:text-amber-500">
                    {formatDuration(occ.overlapsSeconds)} already logged in this slot
                  </div>
                )}
              </div>
              <Button hint={"Don't offer this occurrence"}
                size="sm"
                variant="ghost"
                aria-label="Dismiss this occurrence"
                disabled={busy}
                onClick={() => onDismissOccurrence(occ)}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Mon/Tue/... in the SAME zone as `zonedParts` reads workDate in - never the
 *  browser's local weekday, for the same reason `zonedParts` exists at all. */
function zonedWeekdayLabel(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone, weekday: 'short' }).format(new Date(iso));
}
